import { format } from 'date-fns';
import { entries, isEmpty, isNull, uniqBy } from 'lodash';
import { node, string } from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { createSearchParams, generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { PAGE_TYPES } from 'constants';
import { useFetch, useFilters, useSafeState, useUser } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';

export const ProductsContext = createContext();
export const useProductsContext = () => useContext(ProductsContext);

const ProductsContextProvider = ({ children, initialPageType = null }) => {
  const [searchParams] = useSearchParams();
  const prevSearchParams = useRef(searchParams);
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const { call: travelsCall, loading: loadingTravels } = useFetch({ withSetState: false });
  const { call: leisuresCall, loading: loadingLeisures } = useFetch({ withSetState: false });
  const {
    call: giftcardsCall,
    data: giftcards,
    setData: setGiftcards,
    loading: loadingGiftcards
  } = useFetch({ withSetState: false });
  const [productsTravels, setProductsTravels] = useSafeState(null);
  const [productsLeisures, setProductsLeisures] = useSafeState(null);
  const [page, setPage] = useSafeState(1);
  const [sportCategoryId, setSportCategoryId] = useSafeState(null);

  const {
    getMainFilters,
    getTravelCategoryFilters,
    getPackageFilters,
    getLeisureFilters,
    getSportFilters,
    getGiftcardCategories,
    tourOperatorFilters,
    countryListFilters,
    departureCityFilters,
    travelCategoryFilters,
    leisureCityFilters,
    packageFilters,
    sportCategories,
    leisureCategoryFilters,
    giftcardCategories,
    loadingLeisureCategory,
    loadingSportCategories,
    loadingGiftcardCategories
  } = useFilters(['tourOperators', 'countryList', 'departureCityList']);

  const pageType = useMemo(
    () => initialPageType || searchParams.get('type'),
    [initialPageType, searchParams.get('type')]
  );
  /*
    max items per page for leisures pagination are a multiple of 3
    to allow a 3 columns display on desktop
  */
  const itemsPerPage = { travels: 10, leisures: 12, giftcards: 12 };

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  // useEffect(s) to fetch filters and prevent unecessary calls on pagination
  useEffect(() => {
    const hasFetchedMainFilters =
      !isEmpty(tourOperatorFilters) && !isEmpty(countryListFilters) && !isEmpty(departureCityFilters);

    if (!hasFetchedMainFilters && pageType === PAGE_TYPES.travels) getMainFilters();
  }, [pageType, tourOperatorFilters, countryListFilters, departureCityFilters]);

  useEffect(() => {
    if (pageType === PAGE_TYPES.travels) {
      if (isEmpty(travelCategoryFilters)) getTravelCategoryFilters();
      if (isEmpty(packageFilters)) getPackageFilters();
    }
  }, [pageType, travelCategoryFilters, packageFilters]);

  useEffect(() => {
    const hasFetchedLeisuresFilters = !isEmpty(leisureCityFilters) && !isEmpty(leisureCategoryFilters);

    if (!hasFetchedLeisuresFilters && pageType === PAGE_TYPES.leisures) getLeisureFilters();
  }, [pageType, leisureCityFilters, leisureCategoryFilters]);

  useEffect(() => {
    const hasFetchedSportFilters = !isEmpty(sportCategories) && !isEmpty(leisureCityFilters);
    if (!hasFetchedSportFilters && pageType === PAGE_TYPES.sport) {
      getSportFilters({ withCityFilter: true });
    }
  }, [pageType, sportCategories, leisureCityFilters]);

  useEffect(() => {
    if (!isEmpty(sportCategories)) {
      setSportCategoryId(sportCategories[0].categoryId);
    }
  }, [sportCategories]);

  useEffect(() => {
    const hasFetchedGiftcardsFilters = !isEmpty(giftcardCategories);
    if (!hasFetchedGiftcardsFilters && pageType === PAGE_TYPES.giftcards) getGiftcardCategories();
  }, [pageType, giftcardCategories]);

  // useEffect to fetch products
  useEffect(() => {
    if (isNull(page)) setPage(1);
    if (pageType === PAGE_TYPES.travels) getTravels();
    if ([PAGE_TYPES.leisures, PAGE_TYPES.sport].includes(pageType)) getLeisures();
    if (pageType === PAGE_TYPES.giftcards || initialPageType === PAGE_TYPES.giftcards) getGiftcards();
  }, [pageType, page, initialPageType]);

  useEffect(() => {
    // Reset results list and page when search params change
    if (prevSearchParams.current.toString() !== searchParams.toString()) {
      setProductsTravels(null);
      setProductsLeisures(null);
      setGiftcards(null);
      // Page can not be reset to 1 because it would not trigger a new fetch
      // if the page is already 1
      setPage(null);
      prevSearchParams.current = searchParams;
    }
  }, [searchParams]);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getTravels = async () => {
    if (!currentUser?.serialNumber || loadingTravels) return;

    // Set query object from search params
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    let query = { page, elementByPage: itemsPerPage.travels };

    entries(searchParamsObj).forEach(([key, value]) => {
      if (key === 'type' || (key === 'tourOperatorId' && value === 'touloisirs')) return;

      if (key === 'beginDate' || key === 'endDate') {
        query[key] = format(new Date(value), 'dd/MM/yyyy');
      } else {
        query[key] = value;
      }
    });

    const { data } =
      (await travelsCall({
        service: 'castelis',
        method: 'get',
        /*
        TODO : API does not return filtered results if Content-Type = application/json
        Check if headers can be removed when API is fixed
      */
        headers: { 'Content-Type': null },
        path: `/product/travels/${currentUser.serialNumber}`,
        query
      })) || {};

    if (!isEmpty(data?.body)) {
      setProductsTravels((prev) => {
        return {
          ...data.body,
          travels: [...(prev?.travels || []), ...(data.body?.travels || [])]
        };
      });
    }
  };

  const getLeisures = async () => {
    if (!currentUser?.serialNumber || loadingLeisures) return;

    // Set query object from search params
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    let query = { page, elementByPage: itemsPerPage.leisures };

    entries(searchParamsObj).forEach(([key, value]) => {
      if (key === 'type') return;
      query[key] = value;
    });

    const { data } =
      (await leisuresCall({
        service: 'castelis',
        method: 'get',
        path: `/product/${pageType === PAGE_TYPES.sport ? 'sportWellness' : 'leisures'}/${currentUser.serialNumber}`,
        query
      })) || {};

    if (!isEmpty(data?.body)) {
      setProductsLeisures((prev) => {
        return {
          ...data.body,
          leisures: [...(prev?.leisures || []), ...(data.body?.leisures || [])]
        };
      });
    }
  };

  const getGiftcards = async () => {
    if (loadingGiftcards) return;
    const searchParamsObj = Object.fromEntries(searchParams.entries());
    const filters = {};

    if (searchParamsObj.keywords) {
      filters.title = { $containsi: searchParamsObj.keywords };
    }

    if (searchParamsObj.category?.length > 0) {
      filters.category = { slug: { $eq: searchParamsObj.category.split(',') } };
    }

    const { data } =
      (await giftcardsCall({
        service: appService.getStrapiServiceName(),
        method: 'get',
        path: '/api/giftcards',
        query: {
          pagination: { page },
          filters,
          sort: ['title:asc']
        }
      })) || {};

    if (!isEmpty(data?.body)) {
      setGiftcards((prev) => {
        let gifcardsData = [...(prev?.data || []), ...(data.body?.data || [])];
        gifcardsData = uniqBy(gifcardsData, 'id');
        return { ...data.body, data: gifcardsData };
      });
    }
  };

  const goToProductSow = (product, type) => {
    let path = '';
    switch (type) {
      case PAGE_TYPES.travels:
        path = routes.travelShow.path;
        break;
      case PAGE_TYPES.leisures:
        path = routes.leisureShow.path;
        break;
      case PAGE_TYPES.sport:
        path = routes.sportShow.path;
        break;
      default:
        break;
    }

    if (path) {
      const productPath = generatePath(path, { id: product.productId });

      navigate({ pathname: productPath, search: createSearchParams({ typeItem: product.typeItem }).toString() });
    }
  };

  const providerValues = {
    // Travels
    getTravels,
    productsTravels,
    loadingTravels,
    setProductsTravels,
    // Leisures
    getLeisures,
    productsLeisures,
    setProductsLeisures,
    loadingLeisures,
    // Sport
    getSportFilters,
    sportCategories,
    loadingSportCategories,
    sportCategoryId,
    // Giftcards
    giftcards,
    setGiftcards,
    loadingGiftcards,
    giftcardCategories,
    loadingGiftcardCategories,
    // Pagination
    page,
    setPage,
    pageType,
    itemsPerPage,
    // Filters
    tourOperatorFilters,
    countryListFilters,
    departureCityFilters,
    travelCategoryFilters,
    packageFilters,
    leisureCategoryFilters,
    loadingLeisureCategory,
    leisureCityFilters,
    // Helpers
    goToProductSow
  };

  return <ProductsContext.Provider value={providerValues}>{children}</ProductsContext.Provider>;
};

ProductsContextProvider.propTypes = {
  children: node.isRequired,
  initialPageType: string
};

export default ProductsContextProvider;
